/**
 * Public Quote page
 * Página pública para que los clientes vean y acepten/rechacen presupuestos
 */

import { useState, useEffect } from "react";
import { Icon } from "@/components/ui";
import { generateQuotePDF } from "@/utils/pdfGenerator";
import styles from "./PublicQuote.module.css";

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
}

interface Quote {
  id: string;
  quoteNumber: string;
  clientName: string;
  date: string;
  validUntil: string;
  total: number;
  subtotal: number;
  tax: number;
  status: "draft" | "sent" | "approved" | "rejected" | "expired";
  lineItems: LineItem[];
  notes?: string;
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyLogo?: string;
  pestType?: string;
  rejectionReason?: string;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const months = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
};

export const PublicQuote = () => {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [decision, setDecision] = useState<"approve" | "reject" | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const loadQuote = () => {
      // Obtener el token de la URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      if (!token) {
        setError("Token no válido o presupuesto no encontrado");
        setLoading(false);
        return;
      }

      // Simular carga de presupuesto desde el backend
      // En producción, esto sería una llamada a la API
      setTimeout(() => {
        // Mock data - en producción vendría del backend
        // Usar token para determinar qué presupuesto cargar
        let mockQuote: Quote;

        if (token === "qt_abc123def456ghi7") {
          // Presupuesto rechazado de ejemplo
          mockQuote = {
            id: "5",
            quoteNumber: "PRE-2026-004",
            clientName: "Oficinas Empresariales S.L.",
            date: "2026-01-08",
            validUntil: "2026-02-28",
            total: 975.0,
            subtotal: 805.79,
            tax: 169.21,
            status: "rejected",
            lineItems: [
              {
                id: "1",
                description: "Tratamiento de Hormigas",
                quantity: 1,
                unitPrice: 550,
                discount: 0,
                total: 550,
              },
              {
                id: "2",
                description: "Revisión y Seguimiento",
                quantity: 1,
                unitPrice: 255,
                discount: 0,
                total: 255,
              },
            ],
            notes: "Presupuesto rechazado por el cliente",
            companyName: "BIOSAM - Control de Plagas",
            companyAddress: "Calle Principal, 123, 28001 Madrid",
            companyPhone: "+34 912 345 678",
            companyEmail: "info@biosam.es",
            pestType: "Hormigas",
            rejectionReason:
              "El presupuesto supera nuestro presupuesto anual. Necesitamos una opción más económica.",
          };
        } else {
          // Presupuesto normal por defecto
          mockQuote = {
            id: "1",
            quoteNumber: "PRE-2026-001",
            clientName: "Hotel Míramar",
            date: "2026-01-15",
            validUntil: "2026-02-28",
            total: 1250.0,
            subtotal: 1033.06,
            tax: 216.94,
            status: "sent",
            lineItems: [
              {
                id: "1",
                description: "Control de Plagas",
                quantity: 1,
                unitPrice: 600,
                discount: 0,
                total: 600,
              },
              {
                id: "2",
                description: "Desinfección",
                quantity: 1,
                unitPrice: 450,
                discount: 0,
                total: 450,
              },
            ],
            notes: "Servicio mensual con revisión completa",
            companyName: "BIOSAM - Control de Plagas",
            companyAddress: "Calle Principal, 123, 28001 Madrid",
            companyPhone: "+34 912 345 678",
            companyEmail: "info@biosam.es",
            pestType: "Cucarachas",
          };
        }

        setQuote(mockQuote);
        setLoading(false);
      }, 800);
    };
    loadQuote();
  }, []);

  const handleDecision = (type: "approve" | "reject") => {
    setDecision(type);
    setShowDecisionModal(true);
  };

  const handleSubmitDecision = async () => {
    if (!decision) return;

    setSubmitting(true);

    // Simular envío al backend
    // En producción, esto sería una llamada a la API
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setShowDecisionModal(false);

      // Actualizar el estado del presupuesto
      if (quote) {
        setQuote({
          ...quote,
          status: decision === "approve" ? "approved" : "rejected",
          rejectionReason: decision === "reject" ? rejectionReason : undefined,
        });

        // Si se aprueba, automáticamente se crea una orden de trabajo
        if (decision === "approve") {
          // Auto-crear orden de trabajo
          // TODO: Implementar creación de orden de trabajo cuando el módulo esté disponible
        }
      }
    }, 1000);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Cargando presupuesto...</p>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className={styles.errorContainer}>
        <Icon name="alert-circle" size={64} />
        <h2>Error</h2>
        <p>{error || "No se pudo cargar el presupuesto"}</p>
      </div>
    );
  }

  const isExpired = new Date(quote.validUntil) < new Date();
  const canRespond = quote.status === "sent" && !isExpired && !submitted;

  return (
    <div className={styles.container}>
      <div className={styles.quoteDocument}>
        {/* Header de la empresa */}
        <div className={styles.companyHeader}>
          <div className={styles.companyInfo}>
            <h1>{quote.companyName}</h1>
            <p>{quote.companyAddress}</p>
            <p>
              Tel: {quote.companyPhone} | Email: {quote.companyEmail}
            </p>
          </div>
        </div>

        {/* Información del presupuesto */}
        <div className={styles.quoteHeader}>
          <div>
            <h2>Presupuesto</h2>
            <p className={styles.quoteNumber}>{quote.quoteNumber}</p>
          </div>
          <div className={styles.quoteStatus}>
            <span className={`${styles.statusBadge} ${styles[quote.status]}`}>
              {quote.status === "sent" && "Pendiente"}
              {quote.status === "approved" && "Aceptado"}
              {quote.status === "rejected" && "Rechazado"}
              {quote.status === "expired" && "Expirado"}
            </span>
          </div>
        </div>

        {/* Cliente y fechas */}
        <div className={styles.detailsSection}>
          <div className={styles.detailsColumn}>
            <h3>Cliente</h3>
            <p className={styles.clientName}>{quote.clientName}</p>
          </div>
          <div className={styles.detailsColumn}>
            <h3>Fecha de emisión</h3>
            <p>{formatDate(quote.date)}</p>
          </div>
          <div className={styles.detailsColumn}>
            <h3>Válido hasta</h3>
            <p className={isExpired ? styles.expired : ""}>
              {formatDate(quote.validUntil)}
            </p>
          </div>
          {quote.pestType && (
            <div className={styles.detailsColumn}>
              <h3>Tipo de Plaga</h3>
              <p className={styles.pestType}>{quote.pestType}</p>
            </div>
          )}
        </div>

        {/* Líneas de presupuesto */}
        <div className={styles.itemsSection}>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Cantidad</th>
                <th>Precio Unitario</th>
                <th>Descuento</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.lineItems.map((item) => (
                <tr key={item.id}>
                  <td>{item.description}</td>
                  <td>{item.quantity}</td>
                  <td>{formatCurrency(item.unitPrice)}</td>
                  <td>{item.discount}%</td>
                  <td className={styles.itemTotal}>
                    {formatCurrency(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totales */}
        <div className={styles.totalsSection}>
          <div className={styles.totalRow}>
            <span>Subtotal:</span>
            <span>{formatCurrency(quote.subtotal)}</span>
          </div>
          <div className={styles.totalRow}>
            <span>IVA (21%):</span>
            <span>{formatCurrency(quote.tax)}</span>
          </div>
          <div className={`${styles.totalRow} ${styles.totalFinal}`}>
            <span>Total:</span>
            <span>{formatCurrency(quote.total)}</span>
          </div>
        </div>

        {/* Notas */}
        {quote.notes && (
          <div className={styles.notesSection}>
            <h3>Notas</h3>
            <p>{quote.notes}</p>
          </div>
        )}

        {/* Mensajes de estado */}
        {isExpired && quote.status === "sent" && (
          <div className={styles.alertBox}>
            <Icon name="alert-triangle" size={20} />
            <span>Este presupuesto ha expirado</span>
          </div>
        )}

        {submitted && (
          <div className={styles.successBox}>
            <Icon name="check-circle" size={20} />
            <span>
              {decision === "approve"
                ? "¡Gracias! Has aceptado el presupuesto. Nos pondremos en contacto contigo pronto."
                : "Has rechazado el presupuesto. Gracias por tu respuesta."}
            </span>
          </div>
        )}

        {quote.status === "rejected" && quote.rejectionReason && (
          <div className={styles.rejectionReasonBox}>
            <h4>Motivo del rechazo:</h4>
            <p>{quote.rejectionReason}</p>
          </div>
        )}

        {/* Botón de descarga PDF (siempre visible) */}
        <div className={styles.downloadSection}>
          <button
            className={`${styles.button} ${styles.downloadButton}`}
            onClick={() =>
              generateQuotePDF({
                quoteNumber: quote.quoteNumber,
                clientName: quote.clientName,
                date: quote.date,
                validUntil: quote.validUntil,
                lineItems: quote.lineItems,
                subtotal: quote.subtotal,
                tax: quote.tax,
                total: quote.total,
                notes: quote.notes,
                pestType: quote.pestType,
              })
            }
          >
            <Icon name="download" size={20} />
            Descargar PDF
          </button>
        </div>

        {/* Botones de acción */}
        {canRespond && (
          <div className={styles.actionButtons}>
            <button
              className={`${styles.button} ${styles.rejectButton}`}
              onClick={() => handleDecision("reject")}
            >
              <Icon name="x" size={20} />
              Rechazar
            </button>
            <button
              className={`${styles.button} ${styles.approveButton}`}
              onClick={() => handleDecision("approve")}
            >
              <Icon name="check" size={20} />
              Aceptar Presupuesto
            </button>
          </div>
        )}
      </div>

      {/* Modal de confirmación */}
      {showDecisionModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowDecisionModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                {decision === "approve"
                  ? "Aceptar Presupuesto"
                  : "Rechazar Presupuesto"}
              </h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowDecisionModal(false)}
              >
                <Icon name="x" size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {decision === "approve" ? (
                <p>
                  ¿Confirmas que deseas aceptar este presupuesto? Una vez
                  aceptado, se generará automáticamente una orden de trabajo y
                  nos pondremos en contacto contigo para coordinar los
                  servicios.
                </p>
              ) : (
                <>
                  <p>¿Podrías indicarnos el motivo del rechazo? (opcional)</p>
                  <textarea
                    className={styles.textarea}
                    placeholder="Escribe aquí el motivo del rechazo..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                  />
                </>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={() => setShowDecisionModal(false)}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                className={`${styles.button} ${
                  decision === "approve"
                    ? styles.approveButton
                    : styles.rejectButton
                }`}
                onClick={handleSubmitDecision}
                disabled={submitting}
              >
                {submitting ? "Procesando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
